import { useEffect, useState, useMemo } from 'react';
import { TrendingUp, TrendingDown, Activity } from 'lucide-react';
import api from '@/api';

/**
 * LiveMarketStrip — horizontally-scrollable strip of live asset cards.
 *
 * Shown above the Trends v2 grid on the Investor tab. Surfaces real-time
 * prices + 7-day sparklines from CoinGecko (crypto), Polygon.io (stocks)
 * and Alpha Vantage (forex). Data sourced from /api/pulse/investors which
 * reads the `scored_assets` collection populated by the Pulse pipeline.
 */
export default function LiveMarketStrip() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.getPulseInvestors({ limit: 12 });
        if (!alive) return;
        setAssets(data?.assets || []);
      } catch {
        if (alive) setAssets([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Only render the strip if we have at least 1 asset *with* sparkline history.
  const enriched = useMemo(
    () => (assets || []).filter(a => Array.isArray(a.history_7d) && a.history_7d.length >= 2),
    [assets],
  );

  if (loading) {
    return (
      <div data-testid="live-market-strip-loading" className="flex items-center gap-2 text-xs text-gray-400 px-1 py-2">
        <Activity size={12} className="animate-pulse" />
        Loading live markets…
      </div>
    );
  }
  if (enriched.length === 0) return null;

  return (
    <div data-testid="live-market-strip" className="mb-3">
      <div className="flex items-center gap-1.5 px-1 mb-1.5">
        <Activity size={11} className="text-emerald-500" />
        <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500">
          Live markets · 7-day
        </span>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 snap-x">
        {enriched.map((a) => (
          <SparkCard key={a.asset} asset={a} />
        ))}
      </div>
    </div>
  );
}

function SparkCard({ asset }) {
  const series = asset.history_7d || [];
  const lastPrice = series[series.length - 1] ?? 0;
  const firstPrice = series[0] || lastPrice;
  const change = firstPrice > 0 ? ((lastPrice - firstPrice) / firstPrice) * 100 : 0;
  const up = change >= 0;

  // Decision color + label come from the Pulse score engine.
  const decision = asset.decision || '';
  const decisionTone = decision.includes('BUY')
    ? 'bg-emerald-50 text-emerald-700'
    : decision.includes('SELL') || decision.includes('AVOID')
      ? 'bg-rose-50 text-rose-700'
      : 'bg-amber-50 text-amber-700';

  return (
    <div
      data-testid={`live-market-card-${asset.asset}`}
      className="snap-start flex-shrink-0 w-[148px] bg-white border border-gray-200 rounded-xl p-2.5 shadow-sm"
    >
      <div className="flex items-baseline justify-between gap-1">
        <span className="text-sm font-bold tracking-tight text-gray-900 truncate">
          {asset.asset}
        </span>
        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${decisionTone}`}>
          {decision || asset.asset_type}
        </span>
      </div>

      <div className="text-[11px] tabular-nums text-gray-600 mt-0.5">
        {formatPrice(lastPrice, asset.asset_type)}
      </div>

      <div className="my-1.5">
        <Sparkline series={series} positive={up} />
      </div>

      <div className={`flex items-center gap-0.5 text-[10px] font-semibold ${up ? 'text-emerald-600' : 'text-rose-600'}`}>
        {up ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
        <span className="tabular-nums">{up ? '+' : ''}{change.toFixed(2)}%</span>
      </div>
    </div>
  );
}

function Sparkline({ series, positive, width = 124, height = 30 }) {
  if (!series || series.length < 2) return <div style={{ height }} />;

  const min = Math.min(...series);
  const max = Math.max(...series);
  const range = max - min || 1;

  const stepX = width / (series.length - 1);
  const points = series.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * height;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  const stroke = positive ? '#10B981' : '#EF4444';
  const fill = positive ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)';
  const polyline = points.join(' ');
  // Closed area under the line for the soft fill
  const area = `0,${height} ${polyline} ${width},${height}`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      data-testid="live-market-sparkline"
      aria-hidden="true"
    >
      <polygon points={area} fill={fill} />
      <polyline
        points={polyline}
        fill="none"
        stroke={stroke}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function formatPrice(value, kind) {
  if (!value && value !== 0) return '—';
  if (kind === 'forex') return value.toFixed(4);
  if (value >= 1000) return `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (value >= 1) return `$${value.toFixed(2)}`;
  return `$${value.toFixed(4)}`;
}
